import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

import patchCart from '@/common/api/carts/patchCart';
import patchOrder from '@/common/api/orders/patchOrder';
import Snack from '@/common/components/molecules/Snack/Snack';
import { ITEM_TYPES } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useModalContext } from '@/common/contexts/ModalContext';
import patchQuote from '@/ebat/api/quotes/default/patchQuote';
import patchDraft from '@/ebat/api/quotes/draft/patchDraft';
import patchInstaller from '@/ebat/api/quotes/installer/patchInstaller';
import SelectWorksite from '@/ebat/components/blocks/Modal/Common/SelectWorksite/SelectWorksite';

const useWorksite = ({
  disabled,
  entity,
  isLoading,
  isSuccess,
  onSuccess,
  type,
}) => {
  const { t } = useTranslation();
  const { user, zergRush, isWorksiteRequired } = useAuth();
  const { onCloseModal } = useModalContext();
  const {
    onOpenModal,
    setModalClassName,
    setModalContent,
    setModalSize,
  } = useModalContext();

  /**
   * Return worksite label
   * @type {string}
   */
  const getWorksiteLabel = useMemo(
    () => entity?.worksite?.label || entity?.worksite?.code || '--',
    [entity],
  );

  const getApiRequest = useMemo(() => {
    switch (type) {
      case ITEM_TYPES.order:
        return patchOrder;
      case ITEM_TYPES.quote:
        return patchQuote;
      case ITEM_TYPES.draft:
        return patchDraft;
      case ITEM_TYPES.installer:
        return patchInstaller;
      case ITEM_TYPES.cart:
      default:
        return patchCart;
    }
  }, [type]);

  const getPayload = useCallback(
    (worksiteCode) => {
      const { id } = entity;

      switch (type) {
        case ITEM_TYPES.order:
          return { id, worksite_code: worksiteCode };
        case ITEM_TYPES.draft:
          return { draft: { id, worksite_code: worksiteCode } }; // TODO uniformisation
        case ITEM_TYPES.quote:
          return { quote: { id, worksite_code: worksiteCode } }; // TODO uniformisation
        case ITEM_TYPES.installer:
          return { installer: { id, worksite_code: worksiteCode } }; // TODO uniformisation
        case ITEM_TYPES.cart:
        default:
          return { id, worksite_code: worksiteCode };
      }
    },
    [entity, type],
  );

  /**
   * Update entity mutator
   */
  const {
    mutate: mutatePatchEntity,
    isLoading: isLoadingPatchEntity,
    isSuccess: isSuccessPatchEntity, // if needed
  } = useMutation(getApiRequest);

  /**
   * Update entity to change his worksite
   * @param {string} worksiteCode
   * @param {function} onSuccess callback
   */
  const onUpdateEntity = useCallback(
    ({ worksiteCode, actions, onSuccessCallback = () => null }) => {
      mutatePatchEntity(getPayload(worksiteCode), {
        onSuccess: (data) => {
          onCloseModal();
          onSuccessCallback();
          if (onSuccess) {
            onSuccess(data);
          }

          toast.success(
            <Snack message={t('common:messages:forms.success.default')} />,
          );
        },
        onError: (error) => {
          if (error.response.status === 500) {
            toast.error(
              <Snack
                message={t('common:messages:forms.errors.server_error')}
              />,
            );
          } else {
            if (error.response.data.errors) {
              actions.setErrors(error.response.data.errors);
            }

            toast.error(
              <Snack message={t('common:messages:forms.errors.default')} />,
            );
          }
        },
      });
    },
    [getPayload, mutatePatchEntity, onCloseModal, onSuccess, t],
  );

  /**
   * Check if action is disabled
   * @type {{disabled, loading}}
   */
  const isDisabled = useMemo(() => {
    const loading = isLoading || isLoadingPatchEntity;
    const blocked =
      loading ||
      disabled ||
      zergRush ||
      !user?.functionality_accesses?.worksites;

    return {
      disabled: blocked,
      loading,
      worksiteRequired: blocked || isWorksiteRequired,
    };
  }, [
    disabled,
    isLoading,
    isLoadingPatchEntity,
    isWorksiteRequired,
    user,
    zergRush,
  ]);

  /**
   * Open worksite modal when user click on "Select worksite" button
   * @type {function(): *}
   */
  // TODO is loading on submit button
  const onOpenWorksiteModal = useCallback(
    ({ onSuccessCallback, title }) => {
      setModalContent(
        <SelectWorksite
          disabled={isDisabled.disabled}
          entity={entity}
          isLoading={isDisabled.loading}
          onSelect={(worksiteCode, actions) =>
            onUpdateEntity({ worksiteCode, actions, onSuccessCallback })
          }
          title={title}
        />,
      );
      setModalClassName(cn('modal-select-worksite', 'rs-modal-visible'));
      setModalSize('lg');
      onOpenModal();
    },
    [
      entity,
      isDisabled,
      onOpenModal,
      onUpdateEntity,
      setModalClassName,
      setModalContent,
      setModalSize,
    ],
  );

  return {
    getWorksiteLabel,
    isDisabled,
    isSuccess: isSuccess || isSuccessPatchEntity,
    onOpenWorksiteModal,
    onUpdateEntity,
  };
};

export default useWorksite;
